<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { Play, Pause, Maximize, Volume2, VolumeX, Clapperboard, Globe, Download } from 'lucide-vue-next'
import StarIcon from './StarIcon.vue'
import ShareStarProductButton from './ShareStarProductButton.vue'
import { getCategoryColor } from './vaultCategoryColors.js'
import { useImpressionTracking } from '@/composables/useImpressionTracking'

const props = defineProps({
    item:       { type: Object,  required: true },
    purchasing: { type: Boolean, default: false },
    isCopied:   { type: Boolean, default: false },
})

const emit = defineEmits(['contribute', 'share', 'viewed', 'download'])

const { observeElement } = useImpressionTracking()
const cardEl = ref(null)

onMounted(() => {
    observeElement(cardEl.value, () => emit('viewed', props.item.product_id))
})

// Only vault items carry these fields — plain Exclusive Clips items have
// neither, so this renders nothing for them (unchanged behavior).
const thumbnail = computed(() => (props.item.unlocked ? props.item.thumbnail_unlocked : props.item.thumbnail_locked) || null)

// Unlocked vault videos play inline on click instead of leaving the page —
// lazy-mounted per card so we don't load every unlocked video in the grid at once.
const playingInline = ref(false)

// Custom minimal control set (play/pause, seek, volume, fullscreen) instead
// of the native <video controls> bar — native controls can't be pared down
// to just these on Safari/Firefox (controlsList is Chromium-only), so a
// custom overlay is the only way to get an identical minimal set everywhere.
const videoEl = ref(null)
const videoWrapEl = ref(null)
const isPaused = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const isMuted = ref(false)
const volume = ref(1)

function togglePlay() {
    if (!videoEl.value) return
    if (videoEl.value.paused) videoEl.value.play()
    else videoEl.value.pause()
}

// Controls auto-hide during playback after a few seconds of no interaction,
// same convention as YouTube/native players — but always stay visible while
// paused (nothing to hide from; the center Play button is the focus then).
const CONTROLS_IDLE_MS = 2500
const controlsVisible = ref(true)
let hideControlsTimer = null

function scheduleHideControls() {
    clearTimeout(hideControlsTimer)
    if (isPaused.value) return
    hideControlsTimer = setTimeout(() => { controlsVisible.value = false }, CONTROLS_IDLE_MS)
}

function showControls() {
    controlsVisible.value = true
    scheduleHideControls()
}

function handleVideoDoubleClick() {
    if (document.fullscreenElement) document.exitFullscreen()
}

onBeforeUnmount(() => clearTimeout(hideControlsTimer))

// Only one unlocked clip plays at a time across the whole grid — each card
// is its own component instance with no shared state, so pausing siblings
// has to go through the DOM directly, same approach as
// MembersChoiceProductCard's toggleVideoPlay. Fires on `play` (not the click
// handlers) so it also catches the autoplay that kicks in when a different
// card is opened via "Play your clip" while this one is already playing.
function pauseOtherVideos(event) {
    document.querySelectorAll('video').forEach((v) => {
        if (v !== event.target && !v.paused) v.pause()
    })
}

function toggleFullscreen() {
    if (!videoWrapEl.value) return
    if (document.fullscreenElement) {
        document.exitFullscreen()
    } else if (videoWrapEl.value.requestFullscreen) {
        videoWrapEl.value.requestFullscreen()
    } else if (videoEl.value?.webkitEnterFullscreen) {
        // iPhone Safari has no Fullscreen API for arbitrary elements (iPadOS
        // does) — only the video-specific native fullscreen player, which
        // takes over with its own controls (our overlay doesn't show there).
        videoEl.value.webkitEnterFullscreen()
    }
}

function onSeek(e) {
    if (!videoEl.value) return
    videoEl.value.currentTime = Number(e.target.value)
}

// videoEl can go null between the DOM event firing and this handler running —
// e.g. pauseOtherVideos() or a `v-if` swap (unmounting back to the poster
// thumbnail) tears down the <video> while a timeupdate/loadedmetadata/
// volumechange event from it is still in flight.
function onTimeUpdate() {
    if (!videoEl.value) return
    currentTime.value = videoEl.value.currentTime
}

function onLoadedMetadata() {
    if (!videoEl.value) return
    duration.value = videoEl.value.duration
}

function onVolumeChange() {
    if (!videoEl.value) return
    isMuted.value = videoEl.value.muted
    volume.value = videoEl.value.volume
}

function toggleMute() {
    if (!videoEl.value) return
    videoEl.value.muted = !videoEl.value.muted
}

function onVolumeInput(e) {
    if (!videoEl.value) return
    const value = Number(e.target.value)
    videoEl.value.volume = value
    videoEl.value.muted = value === 0
}

// Drives the filled-in portion of the seek/volume tracks (see the
// `--progress` custom property consumed by the ::-webkit-slider-runnable-track
// / ::-moz-range-track rules below) — plain range inputs don't fill their
// track up to the thumb on their own.
const seekPercent = computed(() => (duration.value ? (currentTime.value / duration.value) * 100 : 0))
const volumePercent = computed(() => (isMuted.value ? 0 : volume.value * 100))

function ctaLabel() {
    if (props.purchasing && props.item.available) return 'Processing…'
    if (!props.item.available) return props.item.total > 0 ? 'Unavailable' : 'Coming soon'
    return `Unlock Access - ${props.item.price.toLocaleString()} stars`
}

const categoryColor = computed(() => getCategoryColor(props.item.category))

</script>

<template>
    <article ref="cardEl" class="card" :class="thumbnail ? 'vault-card' : 'card--padded'">
        <span v-if="item.unlocked && !playingInline" class="chip chip--amber card__leading">
            <StarIcon name="unlock" :size="9" :stroke-width="2.5" /> Unlocked
        </span>
        <span v-else-if="item.hot && !playingInline" class="chip chip--amber card__leading">
            <StarIcon name="flame" :size="9" :stroke-width="2.5" /> Hot
        </span>

        <!-- Vault: poster-style — every vault clip is a vertical/portrait
             video, so the thumbnail leads and dominates, Netflix-poster
             style, instead of the avatar+name row plain Exclusive Clips
             items use. -->
        <template v-if="thumbnail">
            <div class="vault-thumb">
                <div v-if="playingInline && item.unlocked && item.link" ref="videoWrapEl" class="vault-video-wrap"
                     @mousemove="showControls" @touchstart="showControls">
                    <video ref="videoEl" class="vault-thumb__img" :src="item.link" :poster="thumbnail" autoplay playsinline
                           disablePictureInPicture disableRemotePlayback
                           @click="togglePlay(); showControls()" @dblclick="handleVideoDoubleClick"
                           @play="isPaused = false; pauseOtherVideos($event); scheduleHideControls()"
                           @pause="isPaused = true; controlsVisible = true"
                           @timeupdate="onTimeUpdate" @loadedmetadata="onLoadedMetadata" @volumechange="onVolumeChange" />
                    <button v-if="isPaused" type="button" class="vault-video-controls__center-play" aria-label="Play" @click="togglePlay">
                        <Play :size="22" fill="currentColor" />
                    </button>
                    <div class="vault-video-controls" :class="{ 'vault-video-controls--hidden': !controlsVisible }" @click.stop="showControls()">
                        <input
                            type="range" class="vault-video-controls__seek" min="0" :max="duration || 0" step="0.1"
                            :value="currentTime" :style="{ '--progress': seekPercent + '%' }" @input="onSeek" aria-label="Seek"
                        />
                        <div class="vault-video-controls__row">
                            <button type="button" class="vault-video-controls__btn" :aria-label="isPaused ? 'Play' : 'Pause'" @click="togglePlay">
                                <Pause v-if="!isPaused" :size="16" fill="currentColor" />
                                <Play v-else :size="16" fill="currentColor" />
                            </button>
                            <button type="button" class="vault-video-controls__btn" :aria-label="isMuted ? 'Unmute' : 'Mute'" @click="toggleMute">
                                <VolumeX v-if="isMuted || volume === 0" :size="16" />
                                <Volume2 v-else :size="16" />
                            </button>
                            <input
                                type="range" class="vault-video-controls__volume" min="0" max="1" step="0.05"
                                :value="isMuted ? 0 : volume" :style="{ '--progress': volumePercent + '%' }" @input="onVolumeInput" aria-label="Volume"
                            />
                            <button type="button" class="vault-video-controls__btn" aria-label="Fullscreen" @click="toggleFullscreen">
                                <Maximize :size="16" />
                            </button>
                        </div>
                    </div>
                </div>
                <template v-else>
                    <img :src="thumbnail" :alt="item.name" class="vault-thumb__img" />
                    <button v-if="item.unlocked && item.link" class="vault-thumb__play" aria-label="Play video" @click="playingInline = true">
                        <Play :size="22" fill="currentColor" />
                    </button>
                </template>
                <span
                    v-if="item.category && !playingInline && !item.unlocked && !item.hot"
                    class="chip vault-thumb__category"
                    :style="{ color: categoryColor }"
                >{{ item.category }}</span>
                <span v-if="item.duration && !playingInline" class="vault-thumb__duration">{{ item.duration }}</span>
            </div>
            <div class="vault-card__body">
                <div class="vault-card__title-row">
                    <div v-if="item.photo" class="avatar avatar--xs">
                        <img :src="item.photo" :alt="item.name" class="avatar__img" />
                    </div>
                    <div style="min-width:0">
                        <div class="bounty__name vault-card__name" style="font-size:15px">{{ item.name }}</div>
                        <div class="muted small vault-card__name">{{ item.product_name }}</div>
                    </div>
                </div>
                <div v-if="item.linked_scene_title || item.linked_site_name" class="vault-card__links">
                    <a
                        v-if="item.linked_scene_title"
                        :href="route('members2.scene.show', item.linked_scene_slug)"
                        class="vault-card__link"
                    ><Clapperboard :size="11" /> <span>From: {{ item.linked_scene_title }}</span></a>
                    <a
                        v-if="item.linked_site_name"
                        :href="route('members2.site.show', item.linked_site_slug)"
                        class="vault-card__link"
                    ><Globe :size="11" /> <span>{{ item.linked_site_name }}</span></a>
                </div>
                <p v-if="item.product_description" class="muted small vault-card__desc">{{ item.product_description }}</p>
                <div v-if="item.unlocked && item.link" class="cta cta--row">
                    <button v-if="!playingInline" class="btn btn--amber cta--unlock" @click="playingInline = true">
                        <span class="cta--unlock__label">Play your clip</span>
                    </button>
                    <a v-if="item.download_link" :href="item.download_link" class="vault-download-btn" title="Download" aria-label="Download" @click="emit('download', item.product_id)">
                        <Download :size="15" />
                    </a>
                </div>
                <button v-else-if="!item.unlocked" class="btn btn--amber w-full cta cta--unlock" :class="{ 'btn--loading': purchasing }" :disabled="!item.available || purchasing" @click="item.available && emit('contribute')">
                    <span v-if="purchasing && item.available" class="btn-spinner" style="border-color:rgba(58,26,2,0.3);border-top-color:#3a1a02" />
                    <span class="cta--unlock__label">{{ ctaLabel() }}</span>
                </button>
                <ShareStarProductButton v-if="!playingInline" :copied="isCopied" @share="emit('share')" />
            </div>
        </template>

        <!-- Plain Exclusive Clips: unchanged avatar+name layout, no thumbnail. -->
        <template v-else>
            <div class="bounty__head" :class="{ 'bounty__head--leading-clear': item.unlocked || item.hot }">
                <div v-if="item.photo" class="avatar avatar--sm" style="width:44px;height:44px;font-size:15px;background:linear-gradient(135deg,#374151,#4B5563)">
                    <img :src="item.photo" :alt="item.name" class="avatar__img" />
                </div>
                <div style="flex:1;min-width:0">
                    <div class="bounty__name" style="font-size:15px">{{ item.name }}</div>
                    <div class="muted small">{{ item.product_name }}</div>
                </div>
            </div>
            <p v-if="item.product_description" class="muted small" style="margin:0 0 10px">{{ item.product_description }}</p>
            <a v-if="item.unlocked && item.link" class="btn btn--amber w-full cta" :href="item.link" target="_blank" rel="noopener">
                Open your exclusive clips
            </a>
            <button v-else class="btn btn--amber w-full cta" :class="{ 'btn--loading': purchasing }" :disabled="!item.available || purchasing" @click="item.available && emit('contribute')">
                <span v-if="purchasing && item.available" class="btn-spinner" style="border-color:rgba(58,26,2,0.3);border-top-color:#3a1a02" />
                {{ ctaLabel() }}
            </button>
            <ShareStarProductButton :copied="isCopied" @share="emit('share')" />
        </template>
    </article>
</template>

<style scoped>
.muted { color: rgba(255,255,255,0.68); }
.small { font-size: 11px; }

.chip {
    font-size: 9px; font-weight: 900; letter-spacing: 0.10em; text-transform: uppercase;
    padding: 3px 8px; border-radius: 9999px;
    display: inline-flex; align-items: center; gap: 4px;
}
.chip--amber { background: rgba(251,146,60,0.15); color: #FDBA74; border: 1px solid rgba(253,186,116,0.40); }
.card__leading { position: absolute; top: 10px; left: 16px; z-index: 2; }
/* Vault poster cards hide the category pill whenever this shows (see the
   category chip's v-if), freeing up its top-left slot — align there instead
   of the right-side offset tuned for the plain Exclusive Clips layout. Also
   swaps the low-opacity tint for a solid dark backdrop (matching the category
   chip's fix) since it sits directly over a busy thumbnail image here, unlike
   the plain card's dark portlet background. */
.vault-card .card__leading {
    top: 8px; left: 8px; right: auto;
    background: rgba(10,10,14,0.78);
    border: 1.5px solid #FDBA74;
    box-shadow: 0 1px 6px rgba(0,0,0,0.45);
}

/* Poster-style vault card — content here is exclusively vertical/portrait
   video, so the thumbnail is 9:16 (matches the aspect ratio used for vault
   option media elsewhere, e.g. MembersChoiceProductCard's .mc-option__media)
   and flush against the card edges rather than inset with padding. */
.vault-card { display: flex; flex-direction: column; height: 100%; }
/* min-height (not per-field reservations) is what actually guarantees equal
   card height here — the thumbnail above is already fixed via aspect-ratio,
   so this is the only variable part of the card. Sized for the tallest
   realistic case (2 link lines + 2 lines of description); the CTA's
   margin-top:auto (below) absorbs any slack above the button as a single
   gap for cards with less content — a fixed margin there instead left the
   CTA at inconsistent heights across a row whenever some cards had no
   linked scene/site and others did. */
.vault-card__body { padding: 12px 14px 14px; display: flex; flex-direction: column; flex: 1; gap: 2px; min-height: 190px; }
/* Name/subtitle/description are capped (nowrap+ellipsis or a 2-line clamp)
   so an unusually long performer name or description can't grow a card
   past that min-height. */
.vault-card__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }
.vault-card__desc {
    margin: 6px 0 10px;
    display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2;
    line-clamp: 2; overflow: hidden;
}
.vault-card__title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
.vault-card__links { display: flex; flex-direction: column; gap: 3px; margin: 6px 0; }
.vault-card__link {
    display: inline-flex; align-items: center; gap: 5px;
    color: rgba(255,255,255,0.68);
    text-decoration: none;
    font-size: 12px;
}
.vault-card__link svg { flex-shrink: 0; opacity: 0.75; }
.vault-card__link span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vault-card__link:hover { color: #fff; text-decoration: underline; }
.vault-card__link:hover svg { opacity: 1; }

.vault-thumb { position: relative; aspect-ratio: 4 / 5; overflow: hidden; background: linear-gradient(135deg,#1f2937,#111827); }
.vault-thumb__img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }

.vault-video-wrap { position: absolute; inset: 0; }
/* toggleFullscreen() fullscreens this wrapper (not the <video> itself) so the
   custom seek/volume/play overlay below stays visible over it — but that
   means the video keeps .vault-thumb__img's object-fit: cover, tuned for a
   small 4:5 poster card. Stretched to a landscape fullscreen screen, cover
   scales a portrait video up to fill both axes, cropping in tight instead of
   showing the whole frame. Switch to contain (letterboxed) only while
   fullscreen, where showing the full frame matters more than filling edge-to-edge. */
.vault-video-wrap:fullscreen .vault-thumb__img,
.vault-video-wrap:-webkit-full-screen .vault-thumb__img {
    object-fit: contain;
    background: #000;
}
.vault-video-controls {
    position: absolute; left: 0; right: 0; bottom: 0;
    display: flex; flex-direction: column; gap: 4px;
    padding: 6px 8px 8px;
    background: linear-gradient(to top, rgba(0,0,0,0.65), transparent);
    opacity: 1; transition: opacity 200ms ease;
}
/* Auto-hidden during idle playback (see scheduleHideControls/showControls) —
   pointer-events stays on so a mouse resting over the (invisible) seek/volume
   area still counts as movement via @mousemove on the wrapper and brings it
   back, rather than requiring the pointer to leave and re-enter first. */
.vault-video-controls--hidden { opacity: 0; }
.vault-video-controls__row {
    display: flex; align-items: center; gap: 4px;
}
.vault-video-controls__btn {
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    width: 28px; height: 28px; border-radius: 50%; border: none; cursor: pointer;
    background: rgba(0,0,0,0.55); color: #fff;
    transition: background 120ms ease;
}
.vault-video-controls__btn:hover { background: rgba(0,0,0,0.72); }
.vault-video-controls__btn:last-child { margin-left: auto; }

/* Custom-tracked native range inputs — a plain slider track/thumb, not the
   browser's default (bulky, inconsistent styling across browsers) widget. */
.vault-video-controls__seek,
.vault-video-controls__volume {
    -webkit-appearance: none; appearance: none;
    background: transparent; cursor: pointer;
}
.vault-video-controls__seek { width: 100%; height: 14px; }
.vault-video-controls__volume { width: 44px; height: 14px; flex-shrink: 0; }
/* --progress (set inline per-input from seekPercent/volumePercent) fills the
   track up to the thumb with the accent color — plain range inputs don't do
   this on their own, the track is otherwise a single flat color throughout. */
.vault-video-controls__seek::-webkit-slider-runnable-track,
.vault-video-controls__volume::-webkit-slider-runnable-track {
    height: 3px; border-radius: 2px;
    background: linear-gradient(to right, #FDBA74 var(--progress, 0%), rgba(255,255,255,0.3) var(--progress, 0%));
}
.vault-video-controls__seek::-moz-range-track,
.vault-video-controls__volume::-moz-range-track {
    height: 3px; border-radius: 2px; background: rgba(255,255,255,0.3);
}
.vault-video-controls__seek::-moz-range-progress,
.vault-video-controls__volume::-moz-range-progress {
    height: 3px; border-radius: 2px; background: #FDBA74;
}
.vault-video-controls__seek::-webkit-slider-thumb,
.vault-video-controls__volume::-webkit-slider-thumb {
    -webkit-appearance: none; margin-top: -4.5px;
    width: 12px; height: 12px; border-radius: 50%; background: #FDBA74;
}
.vault-video-controls__seek::-moz-range-thumb,
.vault-video-controls__volume::-moz-range-thumb {
    border: none; width: 12px; height: 12px; border-radius: 50%; background: #FDBA74;
}
.vault-thumb__play {
    position: absolute; inset: 0; margin: auto; width: 52px; height: 52px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; border: none; cursor: pointer;
    background: rgba(0,0,0,0.55); color: #fff;
    transition: transform 120ms ease, background 120ms ease;
}
.vault-thumb__play:hover { background: rgba(0,0,0,0.72); transform: scale(1.06); }
/* Same treatment as .vault-thumb__play (the pre-playback big circle button)
   reappearing once the inline video is paused — otherwise there's no visual
   cue left that clicking resumes it, unlike the initial "Play your clip" state. */
.vault-video-controls__center-play {
    position: absolute; inset: 0; margin: auto; width: 52px; height: 52px; z-index: 1;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; border: none; cursor: pointer;
    background: rgba(0,0,0,0.55); color: #fff;
    transition: transform 120ms ease, background 120ms ease;
}
.vault-video-controls__center-play:hover { background: rgba(0,0,0,0.72); transform: scale(1.06); }
/* Solid dark backdrop (not a low-opacity tint of the accent color) — legible
   over any thumbnail regardless of how bright/busy the underlying frame is,
   matching the already-proven high-contrast duration badge below. */
.vault-thumb__category {
    position: absolute; top: 8px; left: 8px;
    background: rgba(10,10,14,0.78);
    box-shadow: 0 1px 6px rgba(0,0,0,0.45);
}
.vault-thumb__duration {
    position: absolute; bottom: 8px; right: 8px;
    font-size: 10px; font-weight: 700; font-variant-numeric: tabular-nums;
    color: #fff; background: rgba(0,0,0,0.72);
    padding: 2px 6px; border-radius: 4px;
}

.bounty__head { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; padding-right: 26px; }
/* The plain (non-vault) card has no thumbnail to overlay the leading
   "Unlocked"/"Hot" badge onto, so it needs to reserve top space itself
   instead of the badge sitting for free over an image. */
.bounty__head--leading-clear { margin-top: 22px; }
.bounty__name { font-family: 'Manrope', sans-serif; font-size: 18px; font-weight: 800; letter-spacing: -0.01em; margin-bottom: 3px; }

.avatar {
    width: 64px; height: 64px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 900; color: #fff;
    flex-shrink: 0; overflow: hidden; position: relative;
}
.avatar__img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: top center; }
.avatar--sm { width: 36px; height: 36px; font-size: 13px; }
.avatar--xs { width: 28px; height: 28px; font-size: 11px; flex-shrink: 0; }

.cta { margin-top: auto; }
/* Bigger star mark + smaller/nowrap label so icon+text always stay on one
   row on narrow vault cards instead of the label wrapping to a second line. */
.cta--unlock { gap: 6px; padding: 10px 14px; }
.cta--unlock__label { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; text-align: center; }

.cta--row { display: flex; gap: 8px; justify-content: center; }
.cta--row .cta--unlock { flex: 1; min-width: 0; }
.vault-download-btn {
    display: inline-flex; align-items: center; justify-content: center;
    /* Fixed height (not left to flex-stretch) — .cta--row has no other item
       to stretch against once "Play your clip" is gone (video already
       playing), so this collapsed to its own ~17px content height and read
       as a squashed pill instead of a matching rounded-square icon button. */
    flex-shrink: 0; width: 40px; height: 40px;
    border-radius: 10px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.16);
    color: #fff;
    text-decoration: none;
    transition: background 0.15s, border-color 0.15s;
}
.vault-download-btn:hover { background: rgba(255,255,255,0.16); border-color: rgba(255,255,255,0.32); }

.btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px 18px; border-radius: 10px; border: none; cursor: pointer;
    font-size: 14px; font-weight: 700; font-family: inherit;
    transition: transform 120ms ease;
    color: #fff;
    text-decoration: none;
}
.btn:active { transform: scale(0.985); }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn--loading { opacity: 0.80; }
.btn--amber { background: #f4b52b; color: #3a1a02; font-weight: 800; }
.w-full { width: 100%; }

.btn-spinner {
    display: inline-block; width: 14px; height: 14px;
    border: 2px solid rgba(255,255,255,0.25);
    border-top-color: currentColor;
    border-radius: 50%;
    animation: btn-spin 0.65s linear infinite;
    flex-shrink: 0;
}
@keyframes btn-spin { to { transform: rotate(360deg); } }
</style>
